import { px } from "@/lib/api";

const W = 900;
const H = 300;
const PAD = { l: 8, r: 80, t: 12, b: 22 };

// levels: [{y, color, label, dash}], marks: [{t, color, label}]
export default function CandleChart({ candles = [], levels = [], marks = [], testId = "candle-chart" }) {
  if (!candles.length) return <div className="h-[220px] flex items-center justify-center label-xs" data-testid={testId}>No candles yet</div>;
  const ys = candles.flatMap((c) => [c.h, c.l]).concat(levels.map((l) => l.y).filter((y) => y != null));
  let lo = Math.min(...ys), hi = Math.max(...ys);
  const padv = (hi - lo) * 0.06 || 1;
  lo -= padv; hi += padv;
  const maxV = Math.max(...candles.map((c) => c.v)) || 1;
  const iw = W - PAD.l - PAD.r, ih = H - PAD.t - PAD.b;
  const step = iw / candles.length;
  const bw = Math.max(2, step * 0.62);
  const Y = (v) => PAD.t + (hi - v) / (hi - lo) * ih;
  const X = (i) => PAD.l + i * step + step / 2;
  const idx = Object.fromEntries(candles.map((c, i) => [c.t, i]));
  const first = candles[0].t, last = candles[candles.length - 1].t;
  // hide levels whose live window lies outside the visible candles
  const shown = levels.filter((l) => l.y != null && !(l.to && l.to < first) && !(l.from && l.from > last));
  const tickEvery = Math.max(6, Math.ceil(candles.length / 9));
  const ly = {};
  [...shown.keys()].sort((a, b) => Y(shown[a].y) - Y(shown[b].y)).reduce((prev, k) => {
    const y = Math.max(Y(shown[k].y) + 3, prev + 11);
    ly[k] = y;
    return y;
  }, -Infinity);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid={testId} preserveAspectRatio="none">
      {candles.map((c, i) => {
        const up = c.c >= c.o;
        const col = up ? "#10B981" : "#EF4444";
        const vh = (c.v / maxV) * ih * 0.18;
        return (
          <g key={c.t}>
            <rect x={X(i) - bw / 2} y={PAD.t + ih - vh} width={bw} height={vh} fill="#334155" opacity="0.5" />
            <line x1={X(i)} x2={X(i)} y1={Y(c.h)} y2={Y(c.l)} stroke={col} strokeWidth="1" />
            <rect x={X(i) - bw / 2} y={Y(Math.max(c.o, c.c))} width={bw} height={Math.max(1, Math.abs(Y(c.o) - Y(c.c)))} fill={col} />
            {i % tickEvery === 0 && <text x={X(i)} y={H - 6} fontSize="10" fill="#64748B" textAnchor={i === 0 ? "start" : "middle"} fontFamily="JetBrains Mono">{c.t}</text>}
          </g>
        );
      })}
      {shown.map((l, i) => {
        const x1 = l.from != null && idx[l.from] != null ? X(idx[l.from]) : PAD.l;  // from before window → left edge
        const x2 = l.to != null && idx[l.to] != null ? X(idx[l.to]) : W - PAD.r;
        const seg = x2 < W - PAD.r - 1;
        return (
          <g key={`l${i}`}>
            <line x1={x1} x2={x2} y1={Y(l.y)} y2={Y(l.y)} stroke={l.color} strokeDasharray={l.dash ? "4 4" : ""} strokeWidth={seg ? 1.5 : 1} opacity="0.85" />
            {seg && <line x1={x2} x2={W - PAD.r} y1={Y(l.y)} y2={Y(l.y)} stroke={l.color} strokeDasharray="1 5" strokeWidth="1" opacity="0.3" />}
            <text x={W - PAD.r + 4} y={ly[i]} fontSize="10" fill={l.color} fontFamily="JetBrains Mono">{l.label} {px(l.y)}</text>
          </g>
        );
      })}
      {marks.filter((m) => idx[m.t] != null).map((m, i) => (
        <g key={`m${i}`}>
          <line x1={X(idx[m.t])} x2={X(idx[m.t])} y1={PAD.t} y2={PAD.t + ih} stroke={m.color} strokeWidth="1" opacity="0.35" />
          <text x={X(idx[m.t]) + 3} y={PAD.t + 10 + (i % 4) * 11} fontSize="10" fill={m.color} fontFamily="JetBrains Mono">{m.label}</text>
        </g>
      ))}
    </svg>
  );
}
